import BackButton from '../../components/Button/IconButton/BackButton';
import Guesses, { NUM_GUESSES_ALLOWED } from './Guesses/Guesses';
import ToppingsSection from './ToppingsSection';
import HOT_DOG_IMAGE from '../../assets/images/hot_dog_game/hot_dog.png';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ToppingStatus, ToppingsEnum } from './toppings_defs';
import { MinigameState } from '../minigame_state_defs';
import History from './Guesses/History';
import DialogueBox from '../../components/DialogueBox/DialogueBox';
import { useSoundEffect } from '../../hooks/useSoundEffect';

import ACCESS_DENIED_SOUND from '../../assets/audio/access_denied.wav';
import KEY_PRESS_SOUND from '../../assets/audio/key_press.wav';
import SAD_TROMBONE_SOUND from '../../assets/audio/sad_trombone.wav';
import SUBMIT_SOUND from '../../assets/audio/submit.wav';
import WIN_SOUND from '../../assets/audio/win.wav';

import './hot-dog-game.css';

interface HotDogProps {
    goToLandingPage: () => void;
    markGameDoneCb: () => void;
}

function HotDogGame(props: HotDogProps) {
    const { goToLandingPage, markGameDoneCb } = props;
    const [gameState, setGameState] = useState<MinigameState>(
        MinigameState.INTRO
    );
    const [guessNumber, setGuessNumber] = useState(0);
    const [staged, setStaged] = useState<ToppingsEnum[]>([]);
    const [correctToppings, setCorrectToppings] = useState<
        (ToppingsEnum | undefined)[][]
    >([
        [undefined, undefined, undefined, undefined],
        [undefined, undefined, undefined, undefined],
        [undefined, undefined, undefined, undefined],
    ]);
    const [toppingStatus, setToppingStatus] = useState<{
        [topping in ToppingsEnum]?: ToppingStatus;
    }>({});
    const [viewHistory, setViewHistory] = useState(false);
    const [pastGuesses, setPastGuesses] = useState<ToppingsEnum[][]>([]);
    const { play: errorSound } = useSoundEffect(ACCESS_DENIED_SOUND, 0.5);
    const { play: keyPressSound } = useSoundEffect(KEY_PRESS_SOUND);
    const { play: submitGuessSound } = useSoundEffect(SUBMIT_SOUND, 0.8);
    const { play: sadTromboneSound } = useSoundEffect(SAD_TROMBONE_SOUND, 0.3);
    const { play: winSound } = useSoundEffect(WIN_SOUND);

    const addToppingByLetter = useCallback(
        (topping: string) => {
            if (gameState !== MinigameState.PLAYING) {
                return;
            }
            if (staged.length < 4) {
                keyPressSound();
                staged.push(
                    ToppingsEnum[
                        topping.toUpperCase() as keyof typeof ToppingsEnum
                    ]
                );
                setStaged([...staged]);
            }
        },
        [gameState, keyPressSound, staged]
    );

    const solution = useMemo(() => {
        // Generate the solution for the game.
        // The toppings will be randomized every time you click into the game.
        // However, there won't ever be duplicate toppings within the same column;
        // every bite will be full of different flavors!
        const solution: { [column: number]: [string, string, string] } = {};
        for (let i = 0; i < 4; i += 1) {
            // For each column, generate 3 distinct toppings.
            const allToppings = Object.keys(ToppingsEnum);
            const originalLength = allToppings.length;
            const index1 = Math.floor(Math.random() * originalLength);
            const index2 = Math.floor(Math.random() * (originalLength - 1));
            const index3 = Math.floor(Math.random() * (originalLength - 2));
            const item1 =
                ToppingsEnum[allToppings[index1] as keyof typeof ToppingsEnum];
            allToppings.splice(index1, 1);
            const item2 =
                ToppingsEnum[allToppings[index2] as keyof typeof ToppingsEnum];
            allToppings.splice(index2, 1);
            const item3 =
                ToppingsEnum[allToppings[index3] as keyof typeof ToppingsEnum];
            solution[i] = [item1, item2, item3];
        }
        return solution;
    }, []);

    const remainingToppings = useMemo(() => {
        const remainingToppings: { [topping: string]: number } = {};
        Object.values(solution).forEach((column) => {
            column.forEach((topping) => {
                if (topping in remainingToppings) {
                    remainingToppings[topping] += 1;
                } else {
                    remainingToppings[topping] = 1;
                }
            });
        });
        return remainingToppings;
    }, [solution]);

    const updateStatus = useCallback(
        (guess: ToppingsEnum, isCorrect: boolean) => {
            if (guess in remainingToppings && remainingToppings[guess] < 0) {
                return;
            }
            if (
                !(guess in remainingToppings) ||
                remainingToppings[guess] === 0
            ) {
                if (!(guess in toppingStatus)) {
                    toppingStatus[guess] = ToppingStatus.GRAY;
                }
            } else {
                if (isCorrect && remainingToppings[guess] === 1) {
                    toppingStatus[guess] = ToppingStatus.GREEN;
                } else {
                    toppingStatus[guess] = ToppingStatus.YELLOW;
                }
            }
            setToppingStatus({ ...toppingStatus });
        },
        [remainingToppings, toppingStatus]
    );

    const checkIfGameOver = useCallback(
        (updatedToppings: (ToppingsEnum | undefined)[][]) => {
            if (!updatedToppings.flat().includes(undefined)) {
                // Win if all correct toppings are set.
                setGameState(MinigameState.WON);
                winSound();
                setViewHistory(false);
            } else if (guessNumber === NUM_GUESSES_ALLOWED - 1) {
                // Lose if all guesses have been used up.
                setGameState(MinigameState.LOST);
                sadTromboneSound();
                setViewHistory(false);
            }
        },
        [guessNumber, sadTromboneSound, winSound]
    );

    const submitGuess = useCallback(() => {
        submitGuessSound();
        // Checks the player's guess against the solution and updates all data structures.
        staged.forEach((guess, columnIndex) => {
            updateStatus(guess, false);
            if (solution[columnIndex].includes(guess)) {
                const rowIndex = solution[columnIndex].indexOf(guess);
                if (!correctToppings[rowIndex][columnIndex]) {
                    // Only update the state if the same topping hasn't already been found.
                    correctToppings[rowIndex][columnIndex] = guess;
                    updateStatus(guess, true);
                    if (remainingToppings[guess] >= 0) {
                        remainingToppings[guess] -= 1;
                    }
                }
            }
        });
        checkIfGameOver(correctToppings);
        setCorrectToppings([...correctToppings]);
        setStaged([]);
        setPastGuesses([...pastGuesses, staged]);
        setGuessNumber(guessNumber + 1);
    }, [
        submitGuessSound,
        staged,
        checkIfGameOver,
        correctToppings,
        pastGuesses,
        guessNumber,
        updateStatus,
        solution,
        remainingToppings,
    ]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (
                gameState !== MinigameState.PLAYING ||
                event.shiftKey ||
                event.altKey ||
                event.ctrlKey
            ) {
                return;
            }
            const pressedKey = event.key;
            if (pressedKey.length === 1) {
                if (
                    Object.keys(ToppingsEnum).includes(pressedKey.toUpperCase())
                ) {
                    addToppingByLetter(pressedKey);
                } else {
                    errorSound();
                }
                return;
            } else if (pressedKey === 'Enter' && staged.length === 4) {
                submitGuess();
                return;
            } else if (pressedKey === 'Backspace') {
                setStaged(staged.slice(0, staged.length - 1));
                return;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [addToppingByLetter, submitGuess, gameState, staged, errorSound]);

    return (
        <div className="hot-dog-game">
            <BackButton onClick={goToLandingPage} />
            <ToppingsSection
                addTopping={addToppingByLetter}
                status={toppingStatus}
            />
            <div className="hot-dog-section">
                <Guesses
                    stagedToppings={staged}
                    correctToppings={correctToppings}
                    numGuesses={guessNumber}
                    submitGuessCb={submitGuess}
                    setViewHistoryCb={setViewHistory}
                />
                <img
                    src={HOT_DOG_IMAGE}
                    alt="Hot dog"
                    className="hot-dog-image"
                />
            </div>
            {viewHistory && (
                <History
                    guesses={pastGuesses}
                    closeHistoryCb={() => setViewHistory(false)}
                />
            )}
            {gameState === MinigameState.INTRO && (
                <DialogueBox
                    text={[
                        "I'm craving a hot dog, but I have too many toppings in my fridge. This is a disaster!",
                        '(Click on a topping or use your keyboard. Each column has 3 distinct toppings,\nbut toppings can be used in more than one column.)',
                        "(After each guess, the color of the topping buttons will give you information.\nGray = I'm not craving it, Yellow = I want more of it, Green = I found all of it!)",
                    ]}
                    afterDialogueCb={() => setGameState(MinigameState.PLAYING)}
                />
            )}
            {gameState === MinigameState.WON && (
                <DialogueBox
                    text={[
                        "Yum, that's exactly what I was craving. Thank you!",
                        '*nom* *nom* *nom*',
                    ]}
                    afterDialogueCb={() => {
                        setGameState(MinigameState.SAD_AGAIN);
                        sadTromboneSound();
                    }}
                />
            )}
            {gameState === MinigameState.SAD_AGAIN && (
                <DialogueBox
                    text={[
                        "Uh oh, I just realized that I'm out of sausages, but I still have so many buns left...\nThis is horrible!",
                    ]}
                    afterDialogueCb={() => {
                        markGameDoneCb();
                        goToLandingPage();
                    }}
                />
            )}
            {gameState === MinigameState.LOST && (
                <DialogueBox
                    text={["Nevermind, I'm not hungry anymore..."]}
                    afterDialogueCb={goToLandingPage}
                />
            )}
        </div>
    );
}

export default HotDogGame;
